"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { MessageCircle, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormDefinition {
  id?: number | string;
  key?: string;
  name?: string;
  spam?: {
    token?: string;
    honeypot?: {
      field: string;
    } | null;
    min_seconds?: number;
  };
  fields?: Array<{
    id: string;
    key: string;
    label?: string;
    type?: string;
    required?: boolean;
    options?: Array<{
      value: string;
      label: string;
    }>;
    validation?: {
      messages?: {
        required?: string;
        invalid?: string;
      };
    };
  }>;
}
export default function LocalTaxi() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [isLoadingDef, setIsLoadingDef] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Wizard step state (1 to 4)
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Contact Details
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  // Step 2: Taxi Booking Details
  const [preferredDate, setPreferredDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [destination, setDestination] = useState("");
  const [transferType, setTransferType] = useState("One-way");
  const [returnDate, setReturnDate] = useState("");
  const [returnPickupTime, setReturnPickupTime] = useState("");
  const [returnPickupLocation, setReturnPickupLocation] = useState("");

  // Step 3: Passenger Details
  const [passengers, setPassengers] = useState("1");
  const [suitcases, setSuitcases] = useState("0");
  const [vehicleType, setVehicleType] = useState("standard_car");
  const [childSeatRequest, setChildSeatRequest] = useState(false);

  // Step 4: Final Details & Message
  const [additionalMessage, setAdditionalMessage] = useState("");

  // Honeypot & spam protection
  const [hpValue, setHpValue] = useState("");
  const fetchTimeRef = useRef<number>(Date.now());

  // Submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message?: string;
  }>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);
  useEffect(() => {
    let isCancelled = false;
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setIsLoadingDef(false);
      return;
    }
    client.pluginRequest("wvc-forms/v1/forms/taxi_service_booking_de").then((res: FormDefinition) => {
      if (!isCancelled) {
        setFormDef(res);
        fetchTimeRef.current = Date.now();
        setIsLoadingDef(false);
      }
    }).catch(() => {
      client.pluginRequest("wvc-forms/v1/forms/taxi_service_booking_de/schema").then((schemaRes: FormDefinition) => {
        if (!isCancelled) {
          setFormDef(schemaRes);
          fetchTimeRef.current = Date.now();
          setIsLoadingDef(false);
        }
      }).catch(() => {
        if (!isCancelled) {
          setLoadError("Die Buchungskonfiguration kann derzeit nicht geladen werden.");
          setIsLoadingDef(false);
        }
      });
    });
    return () => {
      isCancelled = true;
    };
  }, []);
  const validateStep = (step: number): boolean => {
    const stepErrors: Record<string, {
      message: string;
    }> = {};
    if (step === 1) {
      if (!fullName.trim()) stepErrors.fullName = {
        message: "Bitte geben Sie Ihren vollständigen Namen ein."
      };
      if (!phone.trim()) stepErrors.phone = {
        message: "Bitte geben Sie Ihre WhatsApp- oder Telefonnummer an."
      };
      if (!email.trim() || !email.includes("@")) stepErrors.email = {
        message: "Bitte geben Sie eine gültige E-Mail-Adresse ein."
      };
    } else if (step === 2) {
      if (!preferredDate) stepErrors.preferredDate = {
        message: "Bitte wählen Sie Ihr bevorzugtes Datum."
      };
      if (!pickupTime.trim()) stepErrors.pickupTime = {
        message: "Bitte geben Sie Ihre Abholzeit an."
      };
      if (!pickupLocation.trim()) stepErrors.pickupLocation = {
        message: "Bitte geben Sie den Abholort oder das Hotel an."
      };
      if (!destination.trim()) stepErrors.destination = {
        message: "Bitte geben Sie Ihren Zielort an."
      };
      if (!transferType) stepErrors.transferType = {
        message: "Bitte wählen Sie die Transferart."
      };
      if (transferType === "Return") {
        if (!returnDate) stepErrors.returnDate = {
          message: "Bitte wählen Sie das Datum der Rückfahrt."
        };
        if (!returnPickupTime.trim()) stepErrors.returnPickupTime = {
          message: "Bitte geben Sie die Abholzeit für die Rückfahrt an."
        };
        if (!returnPickupLocation.trim()) stepErrors.returnPickupLocation = {
          message: "Bitte geben Sie den Abholort für die Rückfahrt an."
        };
      }
    } else if (step === 3) {
      if (!passengers || Number(passengers) < 1) stepErrors.passengers = {
        message: "Geben Sie mindestens 1 Passagier an."
      };
      if (suitcases === "" || Number(suitcases) < 0) stepErrors.suitcases = {
        message: "Geben Sie die Anzahl der Koffer an."
      };
    }
    if (Object.keys(stepErrors).length > 0) {
      setFieldErrors(prev => ({
        ...prev,
        ...stepErrors
      }));
      return false;
    }
    setFieldErrors(prev => {
      const updated = {
        ...prev
      };
      if (step === 1) {
        delete updated.fullName;
        delete updated.phone;
        delete updated.email;
      } else if (step === 2) {
        delete updated.preferredDate;
        delete updated.pickupTime;
        delete updated.pickupLocation;
        delete updated.destination;
        delete updated.transferType;
        delete updated.returnDate;
        delete updated.returnPickupTime;
        delete updated.returnPickupLocation;
      } else if (step === 3) {
        delete updated.passengers;
        delete updated.suitcases;
      }
      return updated;
    });
    return true;
  };
  const handleNext = () => {
    setGeneralError(null);
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };
  const handleBack = () => {
    setGeneralError(null);
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError(null);
    setFieldErrors({});
    const newErrors: Record<string, {
      message: string;
    }> = {};
    if (!fullName.trim()) newErrors.fullName = {
      message: "Bitte geben Sie Ihren vollständigen Namen ein."
    };
    if (!phone.trim()) newErrors.phone = {
      message: "Bitte geben Sie Ihre WhatsApp- oder Telefonnummer an."
    };
    if (!email.trim() || !email.includes("@")) newErrors.email = {
      message: "Bitte geben Sie eine gültige E-Mail-Adresse ein."
    };
    if (!preferredDate) newErrors.preferredDate = {
      message: "Bitte wählen Sie Ihr bevorzugtes Datum."
    };
    if (!pickupTime.trim()) newErrors.pickupTime = {
      message: "Bitte geben Sie Ihre Abholzeit an."
    };
    if (!pickupLocation.trim()) newErrors.pickupLocation = {
      message: "Bitte geben Sie den Abholort oder das Hotel an."
    };
    if (!destination.trim()) newErrors.destination = {
      message: "Bitte geben Sie Ihren Zielort an."
    };
    if (!transferType) newErrors.transferType = {
      message: "Bitte wählen Sie die Transferart."
    };
    if (transferType === "Return") {
      if (!returnDate) newErrors.returnDate = {
        message: "Bitte wählen Sie das Datum der Rückfahrt."
      };
      if (!returnPickupTime.trim()) newErrors.returnPickupTime = {
        message: "Bitte geben Sie die Abholzeit für die Rückfahrt an."
      };
      if (!returnPickupLocation.trim()) newErrors.returnPickupLocation = {
        message: "Bitte geben Sie den Abholort für die Rückfahrt an."
      };
    }
    if (!passengers || Number(passengers) < 1) newErrors.passengers = {
      message: "Geben Sie mindestens 1 Passagier an."
    };
    if (suitcases === "" || Number(suitcases) < 0) newErrors.suitcases = {
      message: "Geben Sie die Anzahl der Koffer an."
    };
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      if (newErrors.fullName || newErrors.phone || newErrors.email) {
        setCurrentStep(1);
      } else if (newErrors.preferredDate || newErrors.pickupTime || newErrors.pickupLocation || newErrors.destination || newErrors.transferType || newErrors.returnDate || newErrors.returnPickupTime || newErrors.returnPickupLocation) {
        setCurrentStep(2);
      } else if (newErrors.passengers || newErrors.suitcases) {
        setCurrentStep(3);
      }
      return;
    }
    const minSeconds = formDef?.spam?.min_seconds ?? 2;
    const elapsedSeconds = (Date.now() - fetchTimeRef.current) / 1000;
    if (elapsedSeconds < minSeconds) {
      await new Promise(resolve => setTimeout(resolve, (minSeconds - elapsedSeconds + 0.5) * 1000));
    }
    setIsSubmitting(true);
    const client = getWvcClient();
    const token = formDef?.spam?.token || "standard_token";
    const hpField = formDef?.spam?.honeypot?.field || "frms_hp_taxi_booking";
    const transferTypePayload = transferType === "Return" ? "round_trip" : "one_way";
    let vehicleTypePayload = "standard_car";
    if (vehicleType === "minivan") {
      vehicleTypePayload = "minivan";
    } else if (vehicleType === "bus") {
      vehicleTypePayload = "bus";
    }
    const valuesPayload: Record<string, any> = {
      full_name: fullName,
      phone_number: phone,
      email: email,
      preferred_date: preferredDate,
      pickup_time: pickupTime,
      pickup_location: pickupLocation,
      destination: destination,
      transfer_type: transferTypePayload,
      return_date: transferType === "Return" ? returnDate : "",
      return_pickup_time: transferType === "Return" ? returnPickupTime : "",
      return_pickup_location: transferType === "Return" ? returnPickupLocation : "",
      passengers_count: passengers,
      suitcases_count: suitcases,
      vehicle_type: vehicleTypePayload,
      child_seat_request: childSeatRequest ? "child_seat_required" : "",
      additional_message: additionalMessage
    };
    if (formDef?.fields && Array.isArray(formDef.fields)) {
      formDef.fields.forEach(f => {
        if (f.key && valuesPayload[f.key] !== undefined) {
          valuesPayload[f.id] = valuesPayload[f.key];
        }
      });
    }
    const payload = {
      values: valuesPayload,
      token,
      hp: {
        [hpField]: hpValue
      },
      meta: {
        source_url: typeof window !== "undefined" ? window.location.href : "",
        referrer: typeof document !== "undefined" ? document.referrer : ""
      }
    };
    try {
      if (client?.pluginRequest) {
        let res: any;
        try {
          res = await client.pluginRequest("wvc-forms/v1/forms/taxi_service_booking_de/submit", {
            method: "POST",
            data: payload
          });
        } catch {
          res = await client.pluginRequest("wvc-forms/v1/forms/taxi_service_booking_de/entries", {
            method: "POST",
            data: payload
          });
        }
        if (res?.success) {
          if (res?.confirmation?.message) {
            setConfirmationMessage(res.confirmation.message);
          } else {
            setConfirmationMessage("Vielen Dank! Ihre Taxibuchungsanfrage ist eingegangen. Unser Team prüft Ihre Route und kontaktiert Sie in Kürze.");
          }
        } else if (res?.code === "frms_validation_failed" && res?.errors) {
          setFieldErrors(res.errors);
          setGeneralError(res.message || "Bitte überprüfen Sie die markierten Felder.");
        } else {
          setGeneralError(res?.message || "Ihre Anfrage konnte nicht gesendet werden. Bitte kontaktieren Sie uns direkt über WhatsApp.");
        }
      } else {
        setGeneralError("Ihre Anfrage konnte nicht gesendet werden. Bitte kontaktieren Sie uns direkt über WhatsApp.");
      }
    } catch (err: any) {
      setGeneralError(err?.message || "Beim Senden Ihrer Buchung ist ein Netzwerkfehler aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const getVehicleDisplayLabel = (type: string) => {
    switch (type) {
      case "standard_car":
        return "Standard-Pkw – bis zu 4 Personen mit Gepäck";
      case "minivan":
        return "Minivan – bis zu 10 Personen mit Gepäck";
      case "bus":
        return "Bus – für Gruppen ab 10 Personen";
      default:
        return type;
    }
  };
  return <section id="local-taxi-service" data-nav="light" className="relative bg-[#E8F2F1] text-[#333333] py-16 md:py-24 lg:py-28">
      <div className="max-w-[1136px] mx-auto px-6">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left Column: Eyebrow, Heading, Copy, and WhatsApp CTA */}
          <div className="lg:col-span-6 space-y-6">
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2">
              <span className="w-2 h-2 bg-[#D99000] shrink-0" aria-hidden="true" />
              <span className="text-xs sm:text-sm font-bold tracking-wider uppercase text-[#D99000]">
                Punkt-zu-Punkt-Fahrten in der Stadt
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-[#324A6D] leading-[1.14]">
              Lokaler Taxiservice in Hurghada
            </motion.h1>

            <motion.p variants={itemVariants} className="text-lg text-muted-foreground leading-relaxed">
              Neben Flughafentransfers bietet Hurghada Transfer Hub verlässliche,
              private Taxifahrten in ganz Hurghada, El Mamsha, Sheraton Road,
              Dahar und der Hurghada Marina. Ob ein abendlicher Restaurantbesuch,
              Shopping in der Senzo Mall oder Transfers zwischen Resorts – mit unseren
              modernen, klimatisierten Fahrzeugen und geprüften Fahrern werden Preis
              und Verfügbarkeit direkt nach Eingang Ihrer Anfrage verbindlich bestätigt.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="rounded-lg shadow-sm bg-[#27BA2A] text-white hover:bg-[#22a025] transition-[color,background-color,border-color,box-shadow]">
                <Link to="https://wa.me/201010634627" newTab aria-label="Auf WhatsApp mit Hurghada Transfer Hub buchen">
                  <MessageCircle className="size-5" />
                  <span>Auf WhatsApp buchen</span>
                </Link>
              </Button>
            </motion.div>
          </div>

          {/* Right Column: White Booking Wizard Card (4-Step) */}
          <motion.div variants={itemVariants} className="lg:col-span-6">
            <div className="bg-white border border-[#D9E1E2] rounded-2xl shadow-lg p-6 md:p-8 space-y-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-[#324A6D]">
                  Buchen Sie Ihr Taxi
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Schnelle Punkt-zu-Punkt-Transfers mit garantierten Festpreisen in ganz Hurghada.
                </p>
              </div>

              {/* 4-Step Booking Wizard Form */}
              <div className="border-t border-[#E5E7EB] pt-6">
                {isLoadingDef ? <div className="space-y-6">
                    <div className="h-10 bg-slate-100 rounded-lg animate-pulse" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="h-10 bg-slate-100 rounded-lg animate-pulse" />
                      <div className="h-10 bg-slate-100 rounded-lg animate-pulse" />
                    </div>
                    <div className="h-24 bg-slate-100 rounded-lg animate-pulse" />
                    <div className="h-12 w-48 mx-auto bg-slate-100 rounded-lg animate-pulse" />
                  </div> : confirmationMessage ? <div className="text-center py-6 space-y-4">
                    <div className="inline-flex items-center justify-center size-14 rounded-lg bg-emerald-100 text-emerald-700 mx-auto">
                      <CheckCircle2 className="size-8" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#324A6D]">
                      Taxibuchungsanfrage gesendet
                    </h3>
                    <div className="text-muted-foreground text-sm max-w-md mx-auto leading-relaxed" dangerouslySetInnerHTML={{
                  __html: confirmationMessage
                }} />
                    <div className="pt-4">
                      <Button type="button" variant="outline" onClick={() => {
                    setConfirmationMessage(null);
                    setCurrentStep(1);
                    setFullName("");
                    setPhone("");
                    setEmail("");
                    setPreferredDate("");
                    setPickupTime("");
                    setPickupLocation("");
                    setDestination("");
                    setTransferType("One-way");
                    setReturnDate("");
                    setReturnPickupTime("");
                    setReturnPickupLocation("");
                    setPassengers("1");
                    setSuitcases("0");
                    setVehicleType("standard_car");
                    setChildSeatRequest(false);
                    setAdditionalMessage("");
                    setFieldErrors({});
                    setGeneralError(null);
                  }} className="border-[#D9E1E2] text-[#324A6D] hover:bg-[#E8F2F1]/50 rounded-xl px-6">
                        Weitere Anfrage stellen
                      </Button>
                    </div>
                  </div> : <form action="#" method="post" onSubmit={handleSubmit} noValidate className="space-y-6">
                    {/* Spam Honeypot */}
                    {formDef?.spam?.honeypot && <div className="sr-only" style={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  padding: 0,
                  margin: "-1px",
                  overflow: "hidden",
                  clip: "rect(0, 0, 0, 0)",
                  whiteSpace: "nowrap",
                  border: 0
                }} aria-hidden="true">
                        <label htmlFor={formDef.spam.honeypot.field}>Website Decoy</label>
                        <input id={formDef.spam.honeypot.field} name={formDef.spam.honeypot.field} type="text" value={hpValue} tabIndex={-1} autoComplete="off" onChange={e => setHpValue(e.target.value)} />
                      </div>}

                    {/* Progress Indicator */}
                    <div className="mb-6 pb-6 border-b border-[#D9E1E2]">
                      <div className="flex items-center justify-between mb-3 text-xs sm:text-sm">
                        <span className="font-semibold text-[#324A6D]">
                          Schritt {currentStep} von 4:{" "}
                          <span className="text-[#D99000] font-medium">
                            {currentStep === 1 && "Kontaktdaten"}
                            {currentStep === 2 && "Buchungsdetails"}
                            {currentStep === 3 && "Passagierdaten"}
                            {currentStep === 4 && "Prüfen & Absenden"}
                          </span>
                        </span>
                        <span className="text-muted-foreground font-medium text-xs">
                          {Math.round(currentStep / 4 * 100)}% Abgeschlossen
                        </span>
                      </div>

                      <div className="grid grid-cols-4 gap-2">
                        {[1, 2, 3, 4].map((step, i) => {
                      const isCompleted = currentStep > step;
                      const isActive = currentStep === step;
                      return <div key={step} data-index={i} className="flex flex-col gap-1.5">
                              <div className={`h-2 rounded-lg transition-colors duration-200 ${isActive ? "bg-[#D99000]" : isCompleted ? "bg-[#324A6D]" : "bg-[#D9E1E2]"}`} />
                              <span className={`hidden sm:inline-block text-[11px] font-medium truncate ${isActive ? "text-[#D99000]" : isCompleted ? "text-[#324A6D]" : "text-muted-foreground"}`}>
                                {step === 1 && "Kontakt"}
                                {step === 2 && "Buchung"}
                                {step === 3 && "Passagiere"}
                                {step === 4 && "Prüfen & Absenden"}
                              </span>
                            </div>;
                    })}
                      </div>
                    </div>

                    {loadError && <div className="p-3.5 rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm flex items-start gap-2.5">
                        <AlertCircle className="size-4 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold">Hinweis</p>
                          <p className="text-xs mt-0.5">{loadError}</p>
                        </div>
                      </div>}

                    {generalError && <div className="p-3.5 rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm flex items-start gap-2.5">
                        <AlertCircle className="size-4 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold">Fehler</p>
                          <p className="text-xs mt-0.5">{generalError}</p>
                        </div>
                      </div>}

                    {/* STEP 1: Contact Details */}
                    <div className={currentStep === 1 ? "space-y-6" : "hidden"}>
                      {/* Full Name */}
                      <div className="space-y-1.5">
                        <label htmlFor="full_name" className="text-sm font-medium text-foreground block">
                          Vollständiger Name <span className="text-destructive">*</span>
                        </label>
                        <input id="full_name" type="text" placeholder="z. B. Max Mustermann" value={fullName} onChange={e => setFullName(e.target.value)} className={`w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-[#333333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors ${fieldErrors.fullName ? "border-destructive ring-1 ring-destructive" : "border-[#D9E1E2]"}`} required />
                        {fieldErrors.fullName && <p className="text-xs text-destructive">{fieldErrors.fullName.message}</p>}
                      </div>

                      {/* WhatsApp / Phone & Email */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label htmlFor="phone_number" className="text-sm font-medium text-foreground block">
                            WhatsApp- / Telefonnummer <span className="text-destructive">*</span>
                          </label>
                          <input id="phone_number" type="tel" placeholder="+20 100 000 0000" value={phone} onChange={e => setPhone(e.target.value)} className={`w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-[#333333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors ${fieldErrors.phone ? "border-destructive ring-1 ring-destructive" : "border-[#D9E1E2]"}`} required />
                          {fieldErrors.phone && <p className="text-xs text-destructive">{fieldErrors.phone.message}</p>}
                        </div>

                        <div className="space-y-1.5">
                          <label htmlFor="email" className="text-sm font-medium text-foreground block">
                            E-Mail-Adresse <span className="text-destructive">*</span>
                          </label>
                          <input id="email" type="email" placeholder="max@beispiel.de" value={email} onChange={e => setEmail(e.target.value)} className={`w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-[#333333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors ${fieldErrors.email ? "border-destructive ring-1 ring-destructive" : "border-[#D9E1E2]"}`} required />
                          {fieldErrors.email && <p className="text-xs text-destructive">{fieldErrors.email.message}</p>}
                        </div>
                      </div>

                      {/* Step 1 Navigation */}
                      <div className="flex items-center justify-end pt-4 border-t border-[#D9E1E2]">
                        <Button type="button" onClick={handleNext} className="bg-[#D99000] text-white hover:bg-[#D99000]/90 rounded-xl px-7 py-2.5 text-sm font-medium w-full sm:w-auto">
                          Weiter
                        </Button>
                      </div>
                    </div>

                    {/* STEP 2: Taxi Booking Details */}
                    <div className={currentStep === 2 ? "space-y-6" : "hidden"}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label htmlFor="preferred_date" className="text-sm font-medium text-foreground block">
                            Bevorzugtes Datum <span className="text-destructive">*</span>
                          </label>
                          <input id="preferred_date" type="date" value={preferredDate} onChange={e => setPreferredDate(e.target.value)} className={`w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-[#333333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors ${fieldErrors.preferredDate ? "border-destructive ring-1 ring-destructive" : "border-[#D9E1E2]"}`} required />
                          {fieldErrors.preferredDate && <p className="text-xs text-destructive">{fieldErrors.preferredDate.message}</p>}
                        </div>

                        <div className="space-y-1.5">
                          <label htmlFor="pickup_time" className="text-sm font-medium text-foreground block">
                            Abholzeit <span className="text-destructive">*</span>
                          </label>
                          <input id="pickup_time" type="time" value={pickupTime} onChange={e => setPickupTime(e.target.value)} className={`w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-[#333333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors ${fieldErrors.pickupTime ? "border-destructive ring-1 ring-destructive" : "border-[#D9E1E2]"}`} required />
                          {fieldErrors.pickupTime && <p className="text-xs text-destructive">{fieldErrors.pickupTime.message}</p>}
                        </div>
                      </div>

                      {/* Pickup Location & Destination */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label htmlFor="pickup_location" className="text-sm font-medium text-foreground block">
                            Abholort / Hotel <span className="text-destructive">*</span>
                          </label>
                          <input id="pickup_location" type="text" placeholder="z. B. Steigenberger ALDAU Beach Hotel" value={pickupLocation} onChange={e => setPickupLocation(e.target.value)} className={`w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-[#333333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors ${fieldErrors.pickupLocation ? "border-destructive ring-1 ring-destructive" : "border-[#D9E1E2]"}`} required />
                          {fieldErrors.pickupLocation && <p className="text-xs text-destructive">{fieldErrors.pickupLocation.message}</p>}
                        </div>

                        <div className="space-y-1.5">
                          <label htmlFor="destination" className="text-sm font-medium text-foreground block">
                            Zielort <span className="text-destructive">*</span>
                          </label>
                          <input id="destination" type="text" placeholder="z. B. Hurghada Marina oder Senzo Mall" value={destination} onChange={e => setDestination(e.target.value)} className={`w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-[#333333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors ${fieldErrors.destination ? "border-destructive ring-1 ring-destructive" : "border-[#D9E1E2]"}`} required />
                          {fieldErrors.destination && <p className="text-xs text-destructive">{fieldErrors.destination.message}</p>}
                        </div>
                      </div>

                      {/* Transfer Type */}
                      <div className="space-y-1.5">
                        <label htmlFor="transfer_type" className="text-sm font-medium text-foreground block">
                          Transferart <span className="text-destructive">*</span>
                        </label>
                        <select id="transfer_type" value={transferType} onChange={e => setTransferType(e.target.value)} className={`w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-[#333333] focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors ${fieldErrors.transferType ? "border-destructive ring-1 ring-destructive" : "border-[#D9E1E2]"}`} required>
                          <option value="One-way">Einfache Fahrt</option>
                          <option value="Return">Hin- und Rückfahrt</option>
                        </select>
                        {fieldErrors.transferType && <p className="text-xs text-destructive">{fieldErrors.transferType.message}</p>}
                      </div>

                      {/* Return Journey Fields (When transferType === "Return") */}
                      {transferType === "Return" && <div className="space-y-4 pt-2 border-t border-[#D9E1E2]/60">
                          <div className="text-xs font-semibold uppercase tracking-wider text-[#324A6D]">
                            Details zur Rückfahrt
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                              <label htmlFor="return_date" className="text-sm font-medium text-foreground block">
                                Datum der Rückfahrt <span className="text-destructive">*</span>
                              </label>
                              <input id="return_date" type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)} className={`w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-[#333333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors ${fieldErrors.returnDate ? "border-destructive ring-1 ring-destructive" : "border-[#D9E1E2]"}`} required />
                              {fieldErrors.returnDate && <p className="text-xs text-destructive">{fieldErrors.returnDate.message}</p>}
                            </div>

                            <div className="space-y-1.5">
                              <label htmlFor="return_pickup_time" className="text-sm font-medium text-foreground block">
                                Abholzeit Rückfahrt <span className="text-destructive">*</span>
                              </label>
                              <input id="return_pickup_time" type="time" value={returnPickupTime} onChange={e => setReturnPickupTime(e.target.value)} className={`w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-[#333333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors ${fieldErrors.returnPickupTime ? "border-destructive ring-1 ring-destructive" : "border-[#D9E1E2]"}`} required />
                              {fieldErrors.returnPickupTime && <p className="text-xs text-destructive">{fieldErrors.returnPickupTime.message}</p>}
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <label htmlFor="return_pickup_location" className="text-sm font-medium text-foreground block">
                              Abholort Rückfahrt <span className="text-destructive">*</span>
                            </label>
                            <input id="return_pickup_location" type="text" placeholder="z. B. Hurghada Marina oder Hotelname" value={returnPickupLocation} onChange={e => setReturnPickupLocation(e.target.value)} className={`w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-[#333333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors ${fieldErrors.returnPickupLocation ? "border-destructive ring-1 ring-destructive" : "border-[#D9E1E2]"}`} required />
                            {fieldErrors.returnPickupLocation && <p className="text-xs text-destructive">{fieldErrors.returnPickupLocation.message}</p>}
                          </div>
                        </div>}

                      {/* Step 2 Navigation */}
                      <div className="flex items-center justify-between pt-4 border-t border-[#D9E1E2] gap-3 flex-wrap sm:flex-nowrap">
                        <Button type="button" variant="outline" onClick={handleBack} className="border-[#D9E1E2] text-[#324A6D] hover:bg-[#E8F2F1]/50 rounded-xl px-6 py-2.5 text-sm font-medium w-full sm:w-auto order-2 sm:order-1">
                          Zurück
                        </Button>
                        <Button type="button" onClick={handleNext} className="bg-[#D99000] text-white hover:bg-[#D99000]/90 rounded-xl px-7 py-2.5 text-sm font-medium w-full sm:w-auto order-1 sm:order-2 ml-auto">
                          Weiter
                        </Button>
                      </div>
                    </div>

                    {/* STEP 3: Passenger Details */}
                    <div className={currentStep === 3 ? "space-y-6" : "hidden"}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label htmlFor="passengers_count" className="text-sm font-medium text-foreground block">
                            Anzahl der Passagiere <span className="text-destructive">*</span>
                          </label>
                          <input id="passengers_count" type="number" min="1" max="50" placeholder="1" value={passengers} onChange={e => setPassengers(e.target.value)} className={`w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-[#333333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors ${fieldErrors.passengers ? "border-destructive ring-1 ring-destructive" : "border-[#D9E1E2]"}`} required />
                          {fieldErrors.passengers && <p className="text-xs text-destructive">{fieldErrors.passengers.message}</p>}
                        </div>

                        <div className="space-y-1.5">
                          <label htmlFor="suitcases_count" className="text-sm font-medium text-foreground block">
                            Anzahl der Koffer <span className="text-destructive">*</span>
                          </label>
                          <input id="suitcases_count" type="number" min="0" max="50" placeholder="0" value={suitcases} onChange={e => setSuitcases(e.target.value)} className={`w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-[#333333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors ${fieldErrors.suitcases ? "border-destructive ring-1 ring-destructive" : "border-[#D9E1E2]"}`} required />
                          {fieldErrors.suitcases && <p className="text-xs text-destructive">{fieldErrors.suitcases.message}</p>}
                        </div>
                      </div>

                      {/* Vehicle Type (optional) */}
                      <div className="space-y-1.5">
                        <label htmlFor="vehicle_type" className="text-sm font-medium text-foreground block">
                          Fahrzeugtyp <span className="text-xs text-muted-foreground">(Optional)</span>
                        </label>
                        <select id="vehicle_type" value={vehicleType} onChange={e => setVehicleType(e.target.value)} className="w-full rounded-xl border border-[#D9E1E2] bg-background px-3.5 py-2.5 text-sm text-[#333333] focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors">
                          <option value="standard_car">Standard-Pkw – bis zu 4 Personen mit Gepäck</option>
                          <option value="minivan">Minivan – bis zu 10 Personen mit Gepäck</option>
                          <option value="bus">Bus – für Gruppen ab 10 Personen</option>
                        </select>
                      </div>

                      {/* Child Seat Checkbox (optional) */}
                      <div className="flex items-center gap-3 pt-1">
                        <input id="child_seat_request" type="checkbox" checked={childSeatRequest} onChange={e => setChildSeatRequest(e.target.checked)} className="size-4.5 rounded border-[#D9E1E2] text-[#D99000] focus:ring-[#D99000] cursor-pointer" />
                        <label htmlFor="child_seat_request" className="text-sm text-foreground cursor-pointer select-none">
                          Kostenlosen Kindersitz anfragen
                        </label>
                      </div>

                      {/* Step 3 Navigation */}
                      <div className="flex items-center justify-between pt-4 border-t border-[#D9E1E2] gap-3 flex-wrap sm:flex-nowrap">
                        <Button type="button" variant="outline" onClick={handleBack} className="border-[#D9E1E2] text-[#324A6D] hover:bg-[#E8F2F1]/50 rounded-xl px-6 py-2.5 text-sm font-medium w-full sm:w-auto order-2 sm:order-1">
                          Zurück
                        </Button>
                        <Button type="button" onClick={handleNext} className="bg-[#D99000] text-white hover:bg-[#D99000]/90 rounded-xl px-7 py-2.5 text-sm font-medium w-full sm:w-auto order-1 sm:order-2 ml-auto">
                          Weiter
                        </Button>
                      </div>
                    </div>

                    {/* STEP 4: Final Details, Summary & Submit */}
                    <div className={currentStep === 4 ? "space-y-6" : "hidden"}>
                      <div className="space-y-1.5">
                        <label htmlFor="additional_message" className="text-sm font-medium text-foreground block">
                          Zusätzliche Nachricht <span className="text-xs text-muted-foreground">(Optional)</span>
                        </label>
                        <textarea id="additional_message" rows={3} placeholder="Bestimmtes Hotelgebäude, Gepäckdetails oder zeitliche Hinweise..." value={additionalMessage} onChange={e => setAdditionalMessage(e.target.value)} className="w-full rounded-xl border border-[#D9E1E2] bg-background px-3.5 py-2.5 text-sm text-[#333333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D99000] focus:border-transparent transition-colors" />
                      </div>

                      {/* Read-Only Booking Summary */}
                      <div className="bg-[#E8F2F1]/40 rounded-xl p-4 sm:p-5 border border-[#D9E1E2] text-sm space-y-3">
                        <div className="flex items-center justify-between pb-2 border-b border-[#D9E1E2]">
                          <h4 className="font-semibold text-[#324A6D]">Zusammenfassung der Taxibuchung</h4>
                          <span className="text-xs text-muted-foreground">Überprüfen Sie Ihre Fahrtangaben</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-4 text-xs sm:text-sm">
                          <div>
                            <span className="text-muted-foreground block text-xs">Vollständiger Name</span>
                            <span className="font-medium text-foreground">{fullName || "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">WhatsApp / Telefon</span>
                            <span className="font-medium text-foreground">{phone || "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">E-Mail</span>
                            <span className="font-medium text-foreground">{email || "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">Datum & Uhrzeit</span>
                            <span className="font-medium text-foreground">
                              {preferredDate ? `${preferredDate} um ${pickupTime || "Noch offen"}` : "—"}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">Abholort</span>
                            <span className="font-medium text-foreground">{pickupLocation || "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">Zielort</span>
                            <span className="font-medium text-foreground">{destination || "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">Transferart</span>
                            <span className="font-medium text-foreground">
                              {transferType === "Return" ? "Hin- und Rückfahrt" : "Einfache Fahrt"}
                            </span>
                          </div>
                          {transferType === "Return" && <>
                              <div>
                                <span className="text-muted-foreground block text-xs">Rückfahrt Datum & Uhrzeit</span>
                                <span className="font-medium text-foreground">
                                  {returnDate ? `${returnDate} um ${returnPickupTime || "Noch offen"}` : "—"}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-xs">Abholort Rückfahrt</span>
                                <span className="font-medium text-foreground">{returnPickupLocation || "—"}</span>
                              </div>
                            </>}
                          <div>
                            <span className="text-muted-foreground block text-xs">Gewähltes Fahrzeug</span>
                            <span className="font-medium text-foreground">{getVehicleDisplayLabel(vehicleType)}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">Passagiere</span>
                            <span className="font-medium text-foreground">{passengers}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">Koffer</span>
                            <span className="font-medium text-foreground">{suitcases}</span>
                          </div>
                          {childSeatRequest && <div>
                              <span className="text-muted-foreground block text-xs">Kindersitz</span>
                              <span className="font-medium text-foreground">Angefragt</span>
                            </div>}
                          {additionalMessage && <div className="sm:col-span-2">
                              <span className="text-muted-foreground block text-xs">Zusätzliche Nachricht</span>
                              <span className="font-medium text-foreground">{additionalMessage}</span>
                            </div>}
                        </div>
                      </div>

                      {/* Step 4 Navigation with Submit CTA */}
                      <div className="flex items-center justify-between pt-4 border-t border-[#D9E1E2] gap-3 flex-wrap sm:flex-nowrap">
                        <Button type="button" variant="outline" onClick={handleBack} disabled={isSubmitting} className="border-[#D9E1E2] text-[#324A6D] hover:bg-[#E8F2F1]/50 rounded-xl px-6 py-2.5 text-sm font-medium w-full sm:w-auto order-2 sm:order-1">
                          Zurück
                        </Button>

                        <Button type="submit" disabled={isSubmitting} className="bg-[#D99000] text-white hover:bg-[#D99000]/90 rounded-2xl py-3 px-8 text-base font-medium shadow-sm transition-colors flex items-center justify-center gap-2 h-auto w-full sm:w-auto order-1 sm:order-2 ml-auto">
                          {isSubmitting ? <>
                              <Loader2 className="size-5 animate-spin" />
                              <span>Anfrage wird gesendet...</span>
                            </> : <span>Taxibuchungsanfrage absenden</span>}
                        </Button>
                      </div>
                    </div>
                  </form>}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}